import type { Provider } from '@angular/core';
import {
  BarChart,
  GaugeChart,
  HeatmapChart,
  LineChart,
  MapChart,
  PieChart,
  RadarChart,
  ScatterChart,
} from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { provideEchartsCore } from 'ngx-echarts';

echarts.use([
  LineChart,
  BarChart,
  PieChart,
  RadarChart,
  ScatterChart,
  GaugeChart,
  HeatmapChart,
  MapChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  ToolboxComponent,
  RadarComponent,
  DataZoomComponent,
  // Heatmap / choropleth map need visualMap to map values onto the color ramp.
  VisualMapComponent,
  UniversalTransition,
  // Deliberately omit LegacyGridContainLabel — ECharts 6's legacy
  // containLabel path + default outerBounds can burn the main thread when
  // dashboard cards mount (reporting charts-grid freeze). We pin grids with
  // `outerBoundsMode: 'none'` in option builders instead.
  CanvasRenderer,
]);

/**
 * Registers the tree-shaken Apache ECharts core (line/bar/pie/area/radar/
 * scatter/gauge/heatmap/map series, grid, legend, tooltip, toolbox, radar,
 * dataZoom, visualMap, universal transition, canvas renderer) and wires it
 * into `ngx-echarts` via `provideEchartsCore`, so
 * {@link ChartRendererComponent} can render `EChartsOption` configs produced
 * by `generateChartOptions` and the dedicated radar/scatter/gauge/heatmap/
 * map-bar-morph builders.
 *
 * `echarts.use()` registers modules on the shared `echarts/core` module
 * instance, so call this **once** from each app's root `ApplicationConfig`
 * — never per-feature.
 *
 * @example
 * providers: [provideCharts(), ...]
 */
export function provideCharts(): Provider {
  return provideEchartsCore({ echarts });
}
